import { FormEvent, useState } from "react";
import type { SalesDemoMessage } from "./types";
import { EmptyState, StatusBadge } from "./restaurant-demo-v4-ui";
import type { RestaurantDemoV5PanelProps } from "./restaurant-demo-v5-types";
import {
  RestaurantAgentV7ClientError,
  resolveRestaurantAgentV7Proposal,
  runRestaurantAgentV7,
  type RestaurantAgentV7Action,
  type RestaurantAgentV7HistoryMessage,
  type RestaurantAgentV7Plan,
  type RestaurantAgentV7PlanContext,
  type RestaurantAgentV7Proposal,
  type RestaurantAgentV7QuickChoice,
} from "./restaurant-agent-v7-client";

interface RuntimeMeta {
  readonly mode: "idle" | "real" | "fallback" | "unavailable";
  readonly providerKey: string | null;
  readonly model: string | null;
  readonly rounds: number;
  readonly steps: number;
  readonly detail: string;
}

interface ActivePlanState {
  readonly plan: RestaurantAgentV7Plan;
  readonly context: RestaurantAgentV7PlanContext;
}

type ProposalUiStatus = "pending" | "applying" | "applied" | "rejected" | "stale" | "failed";
interface ProposalUi { readonly proposal: RestaurantAgentV7Proposal; readonly status: ProposalUiStatus; }
interface TurnStructure {
  readonly plan: RestaurantAgentV7Plan | null;
  readonly planContext: RestaurantAgentV7PlanContext | null;
  readonly proposals: readonly ProposalUi[];
  readonly quickChoices: readonly RestaurantAgentV7QuickChoice[];
}

function destination(capability: string) {
  if (capability.startsWith("catalog") || capability.startsWith("modifier") || capability.startsWith("recipe")) return capability.startsWith("recipe") ? "stock" : "carta";
  if (capability.startsWith("reservation")) return "reservas";
  if (capability.startsWith("seating")) return "mesas";
  if (capability.startsWith("order") || capability.startsWith("pos")) return capability.startsWith("pos") ? "pos" : "pedidos";
  if (capability.startsWith("inventory")) return "stock";
  if (capability.startsWith("customer")) return "clientes";
  return "inicio";
}

function historyFrom(messages: readonly SalesDemoMessage[]): readonly RestaurantAgentV7HistoryMessage[] {
  return messages
    .filter((message) => message.id !== "agent-welcome-v7")
    .map((message) => ({ role: message.role === "customer" ? "customer" as const : "assistant" as const, text: message.text }))
    .slice(-16);
}

function stepIcon(status: RestaurantAgentV7Plan["steps"][number]["status"]) {
  if (status === "succeeded") return "✓";
  if (status === "waiting_approval") return "!";
  if (status === "failed") return "×";
  return "·";
}

function planTone(status: RestaurantAgentV7Plan["status"]): "success" | "warning" | "danger" | "info" {
  if (status === "completed") return "success";
  if (status === "waiting_approval" || status === "partial") return "warning";
  if (status === "failed") return "danger";
  return "info";
}

function PlanCard({ plan }: { plan: RestaurantAgentV7Plan }) {
  return <section className="rdv7-plan-card" aria-label="Plan operacional">
    <header>
      <div><span className="rdv4-eyebrow">Plan operacional</span><strong>{plan.goal}</strong></div>
      <StatusBadge tone={planTone(plan.status)}>{plan.status === "completed" ? "Completado" : plan.status === "waiting_approval" ? "Esperando aprobación" : plan.status === "partial" ? "Parcial" : "Con errores"}</StatusBadge>
    </header>
    <ol>{plan.steps.map((step) => <li className={`status-${step.status}`} key={step.id}>
      <span className="rdv7-plan-icon">{stepIcon(step.status)}</span>
      <div><strong>{step.label}</strong><small>{step.detail}</small><code>{step.operation}</code></div>
    </li>)}</ol>
  </section>;
}

function ProposalCard({ item, onDecision, onRecalculate }: {
  item: ProposalUi;
  onDecision: (decision: "apply" | "reject") => void;
  onRecalculate: () => void;
}) {
  const proposal = item.proposal;
  const settled = item.status === "applied" || item.status === "rejected";
  return <section className={`rdv7-proposal-card status-${item.status}`} aria-label={proposal.title}>
    <header>
      <div><span className="rdv4-eyebrow">Requiere aprobación</span><h4>{proposal.title}</h4><p>{proposal.summary}</p></div>
      <span className="rdv7-risk-pill">Acción sensible</span>
    </header>
    {proposal.items.length ? <div className="rdv7-proposal-items">{proposal.items.slice(0, 12).map((proposalItem, index) => <div key={`${proposalItem.label}-${index}`}>
      <strong>{proposalItem.label}</strong>
      {proposalItem.before || proposalItem.after ? <span>{proposalItem.before ?? "—"}<b>→</b>{proposalItem.after ?? "—"}</span> : null}
      {proposalItem.detail ? <small>{proposalItem.detail}</small> : null}
    </div>)}</div> : null}
    {proposal.items.length > 12 ? <small className="rdv7-more-items">+{proposal.items.length - 12} elementos más</small> : null}
    <footer>
      {item.status === "pending" ? <><button type="button" className="rdv4-button-secondary" onClick={() => onDecision("reject")}>Cancelar</button><button type="button" className="rdv4-button-primary" onClick={() => onDecision("apply")}>Confirmar cambios</button></> : null}
      {item.status === "applying" ? <span className="rdv7-proposal-state">Aplicando y revalidando…</span> : null}
      {item.status === "applied" ? <span className="rdv7-proposal-state success">✓ Aplicado</span> : null}
      {item.status === "rejected" ? <span className="rdv7-proposal-state">Cancelado</span> : null}
      {item.status === "stale" ? <><span className="rdv7-proposal-state warning">El negocio cambió desde la previsualización.</span><button type="button" className="rdv4-button-primary" onClick={onRecalculate}>Recalcular propuesta</button></> : null}
      {item.status === "failed" ? <span className="rdv7-proposal-state danger">No se pudo aplicar.</span> : null}
      {settled ? <small>La decisión se ejecutó directamente contra backend, sin enviar otro mensaje al modelo.</small> : null}
    </footer>
  </section>;
}

export function RestaurantAgentPanelV7(props: RestaurantDemoV5PanelProps) {
  const { state, v5 } = props;
  const quickPrompts = [
    "¿Cómo está el negocio hoy?",
    "Subí todas las pizzas un 10%",
    "Creá un área Terraza con dos mesas para 4 personas, creá una categoría Cervezas, agregá Patagonia a $6000, registrá 24 unidades en stock y subí todas las pizzas un 10%.",
    "Creá una mesa para 6 en Terraza y reservámela mañana a las 21 para Lucía.",
    "Vendé por POS una Napolitana grande con extra muzzarella a Alexi, con 10% de descuento, pago en efectivo.",
    "Entraron 10 kg de muzzarella",
  ];
  const [messages, setMessages] = useState<readonly SalesDemoMessage[]>([
    { id: "agent-welcome-v7", role: "assistant", text: `Soy el operador IA V7.1 de ${state.businessName}. Prevalido planes completos antes de mutar Restaurant OS, reparo argumentos con contratos exactos y uso botones para aprobaciones sensibles.` },
  ]);
  const [structures, setStructures] = useState<Record<string, TurnStructure>>({});
  const [activePlan, setActivePlan] = useState<ActivePlanState | null>(null);
  const [actions, setActions] = useState<readonly RestaurantAgentV7Action[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [runtime, setRuntime] = useState<RuntimeMeta>({ mode: "idle", providerKey: null, model: null, rounds: 0, steps: 0, detail: "Esperando el primer mensaje" });

  function rememberPlan(plan: RestaurantAgentV7Plan | null, context: RestaurantAgentV7PlanContext | null) {
    if (!plan || !context || plan.status === "completed" || plan.status === "failed") {
      setActivePlan(null);
      return;
    }
    setActivePlan({ plan, context });
  }

  async function run(text: string) {
    const clean = text.trim();
    if (!clean || busy) return;
    const nonce = Date.now();
    const assistantId = `agent-assistant-v7-${nonce}`;
    const history = historyFrom(messages);
    setMessages((current) => [...current, { id: `agent-user-v7-${nonce}`, role: "customer", text: clean }]);
    setInput("");
    setBusy(true);
    try {
      const result = await runRestaurantAgentV7({
        message: clean,
        history,
        state,
        v5,
        activePlan: activePlan?.plan ?? null,
        activePlanContext: activePlan?.context ?? null,
      });
      setMessages((current) => [...current, { id: assistantId, role: "assistant", text: result.response }]);
      setStructures((current) => ({
        ...current,
        [assistantId]: {
          plan: result.plan,
          planContext: result.planContext,
          proposals: result.proposals.map((proposal) => ({ proposal, status: "pending" as const })),
          quickChoices: result.quickChoices,
        },
      }));
      rememberPlan(result.plan, result.planContext);
      setActions((current) => [...current, ...result.actions].slice(-80));
      props.setState(result.state);
      props.setV5(result.v5);
      setRuntime({ mode: result.runtime.mode, providerKey: result.runtime.providerKey, model: result.runtime.model, rounds: result.runtime.rounds, steps: result.runtime.steps, detail: result.runtime.terminalReason });
    } catch (error) {
      const clientError = error instanceof RestaurantAgentV7ClientError ? error : new RestaurantAgentV7ClientError("agent_runtime_unavailable", error instanceof Error ? error.message : "Agent Runtime no disponible");
      const configurationMissing = clientError.code === "agent_provider_unconfigured";
      const explanation = configurationMissing
        ? "El Runtime V7.1 está conectado, pero Platform API todavía no tiene una API key configurada. No ejecuté ninguna acción."
        : "El Runtime V7.1 no pudo completar este turno. Los planes inválidos se descartan antes de dejar mutaciones parciales.";
      setMessages((current) => [...current, { id: assistantId, role: "assistant", text: explanation }]);
      setRuntime({ mode: "unavailable", providerKey: null, model: null, rounds: 0, steps: 0, detail: `${clientError.code}: ${clientError.message}` });
      props.notify(configurationMissing ? "Falta configurar la API key del Agent Runtime" : "Agent Runtime temporalmente no disponible", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function decide(messageId: string, proposal: RestaurantAgentV7Proposal, decision: "apply" | "reject") {
    const structure = structures[messageId];
    if (!structure) return;
    setStructures((current) => ({ ...current, [messageId]: { ...current[messageId]!, proposals: current[messageId]!.proposals.map((item) => item.proposal.id === proposal.id ? { ...item, status: "applying" } : item) } }));
    try {
      const result = await resolveRestaurantAgentV7Proposal({ decision, proposal, state, v5, plan: structure.plan, planContext: structure.planContext });
      props.setState(result.state);
      props.setV5(result.v5);
      setActions((current) => [...current, result.action, ...result.continuationActions].slice(-80));
      const continuedContext = result.planContext ?? structure.planContext;
      rememberPlan(result.plan, continuedContext);
      setStructures((current) => {
        const previous = current[messageId]!;
        const old = previous.proposals.map((item) => item.proposal.id === proposal.id ? { ...item, status: decision === "apply" ? "applied" as const : "rejected" as const } : item);
        const known = new Set(old.map((item) => item.proposal.id));
        const appended = result.proposals.filter((next) => !known.has(next.id)).map((next) => ({ proposal: next, status: "pending" as const }));
        return { ...current, [messageId]: { ...previous, plan: result.plan ?? previous.plan, planContext: continuedContext, proposals: [...old, ...appended], quickChoices: [...previous.quickChoices, ...result.quickChoices] } };
      });
      props.notify(decision === "apply" ? (result.continuationActions.length ? "Cambio aplicado y plan reanudado" : "Cambio aplicado") : "Propuesta cancelada");
    } catch (error) {
      const clientError = error instanceof RestaurantAgentV7ClientError ? error : new RestaurantAgentV7ClientError("approval_failed", error instanceof Error ? error.message : "Approval falló");
      const status: ProposalUiStatus = clientError.code === "proposal_stale" || clientError.code === "proposal_expired" ? "stale" : "failed";
      setStructures((current) => ({ ...current, [messageId]: { ...current[messageId]!, proposals: current[messageId]!.proposals.map((item) => item.proposal.id === proposal.id ? { ...item, status } : item) } }));
      props.notify(clientError.code === "proposal_stale" ? "La propuesta quedó desactualizada; recalculala" : "No se pudo resolver la propuesta", "danger");
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); void run(input); }

  const runtimeBadge = runtime.mode === "real"
    ? <StatusBadge tone="success">Runtime V7.1 real</StatusBadge>
    : runtime.mode === "fallback"
      ? <StatusBadge tone="warning">Fallback seguro</StatusBadge>
      : runtime.mode === "unavailable"
        ? <StatusBadge tone="danger">Runtime sin conexión</StatusBadge>
        : <StatusBadge tone="info">V7.1 listo</StatusBadge>;

  return <div className="rdv4-view rdv4-agent-view rdv6-agent-view rdv7-agent-view">
    <div className="rdv4-agent-quick-prompts"><span>Probá robustez real:</span>{quickPrompts.map((prompt) => <button type="button" key={prompt} disabled={busy} onClick={() => void run(prompt)}>{prompt}</button>)}</div>
    <div className="rdv4-agent-layout">
      <section className="rdv4-surface rdv4-agent-chat">
        <header><div className="rdv4-agent-avatar">AI</div><div><strong>Operador IA de {state.businessName}</strong><small>{runtime.model ? `${runtime.providerKey} · ${runtime.model}` : "Preflight + Planner + Capability Layer + Approval Engine"}</small></div>{runtimeBadge}</header>
        {activePlan ? <div className="rdv7-active-plan-banner"><span>{activePlan.plan.status === "waiting_approval" ? "Aprobación pendiente" : "Plan pendiente"}</span><strong>{activePlan.plan.goal}</strong><small>Una respuesta corta puede continuar este plan; un objetivo nuevo no hereda sus pasos.</small></div> : null}
        <div className="rdv4-agent-messages">
          {messages.map((message) => {
            const structure = structures[message.id];
            return <div className="rdv7-message-stack" key={message.id}>
              <div className={`rdv4-agent-message ${message.role}`}><span>{message.text}</span></div>
              {message.role === "assistant" && structure?.plan ? <PlanCard plan={structure.plan} /> : null}
              {message.role === "assistant" ? structure?.proposals.map((proposalItem) => <ProposalCard key={proposalItem.proposal.id} item={proposalItem} onDecision={(decision) => void decide(message.id, proposalItem.proposal, decision)} onRecalculate={() => void run(`Recalculá la propuesta: ${proposalItem.proposal.summary}`)} />) : null}
              {message.role === "assistant" && structure?.quickChoices.length ? <div className="rdv7-choice-row"><span>Opciones disponibles</span>{structure.quickChoices.map((choice) => <button type="button" key={choice.id} disabled={busy} onClick={() => void run(choice.message)}>{choice.label}</button>)}</div> : null}
            </div>;
          })}
          {busy ? <div className="rdv4-agent-message assistant"><span>Prevalidando, planificando y ejecutando sobre Restaurant OS…</span></div> : null}
        </div>
        <form className="rdv4-agent-composer" onSubmit={submit}><input value={input} onChange={(event) => setInput(event.target.value)} disabled={busy} placeholder="Pedile una consulta, una acción o varias cosas juntas…" /><button className="rdv4-button-primary" disabled={!input.trim() || busy}>{busy ? "Ejecutando…" : "Enviar"}</button></form>
      </section>
      <aside className="rdv4-surface rdv4-agent-actions">
        <header><span className="rdv4-eyebrow">Ejecución verificable</span><h3>Qué hizo Restaurant OS</h3><p>Mostramos operaciones y planes verificables, nunca razonamiento privado del modelo.</p></header>
        {runtime.mode !== "idle" ? <div className="rdv6-agent-runtime-meta"><span>Runtime</span><strong>{runtime.mode === "real" ? "Real" : runtime.mode === "fallback" ? "Fallback seguro" : "No disponible"}</strong>{runtime.model ? <small>{runtime.providerKey} · {runtime.model}</small> : null}<small>{runtime.detail}</small>{runtime.rounds > 0 ? <small>{runtime.rounds} ronda(s) · {runtime.steps} paso(s)</small> : null}</div> : null}
        {actions.length === 0 ? <EmptyState title="Sin operaciones todavía" description={busy ? "El agente está trabajando." : "Escribí una consulta o una acción para ver las capacidades ejecutadas."} /> : <ol>{actions.map((action, index) => <li key={`${action.capability}-${index}`}><div className="rdv4-action-number">{index + 1}</div><div><strong>{action.label}</strong><p>{action.detail}</p><small>{action.capability} · {action.status === "succeeded" ? "OK" : action.status === "waiting_approval" ? "APROBACIÓN" : "BLOQUEADA"}</small><button type="button" onClick={() => props.changeSection(destination(action.capability) as Parameters<typeof props.changeSection>[0])}>Ver en administración →</button></div></li>)}</ol>}
      </aside>
    </div>
  </div>;
}
